import {
	TFileId,
	IItemForGetShortInfo,
	IFileData,
	TTypeOfServiceProvided,
	ICreateCustomer,
	TUseTipsByDefault,
} from 'types';
import { createContext } from 'react';

export type TCreateCustomerSelectedItem = Nullable<string>;
export type TCreateCustomerSelectedClientType = null | TTypeOfServiceProvided;
export type TSourceUrlLocally = Nullable<string>;
export type TTypeOfCreateCustomerModalWindow =
	| null
	| 'toConfirmFieldsClearing'
	| 'toConfirmSubmit'
	| 'success'
	| 'error'
	| 'imageFileSizeExceeded'
	| 'htmlFileSizeExceeded'
	| 'fileUploadError'
	| 'invalidFileFormat'
	| 'customerNameAlreadyExist';

export interface ICreateCustomerState {
	fileId: TFileId;
	sourceUrlLocally: TSourceUrlLocally;
	name: string;
	email: string;
	emailBodyTemplate: string;
	clientType: TCreateCustomerSelectedClientType;
	billTemplateFileData: IFileData;
	billAdvertisingImageData: IFileData;
	billAdvertisingLink: Nullable<string>;
	priceType: Nullable<string>;
	tips0: null | number;
	tips1: null | number;
	tips2: null | number;
	tips3: null | number;
	defaultTips: TUseTipsByDefault;
	ads: string;
	mixpanelToken: string;
	stopListSyncIntervalMinutes: Nullable<number>;
	fetchedLanguages: IItemForGetShortInfo[];
	IDSelectedCustomerDefaultLanguage: TCreateCustomerSelectedItem;
	IDsSelectedCustomerLanguages: string[];
	dataForSubmit: ICreateCustomer | null;
	typeOfModalWindow: TTypeOfCreateCustomerModalWindow;
}

export const createCustomerInitialState: ICreateCustomerState = {
	fileId: null,
	sourceUrlLocally: null,
	name: '',
	email: '',
	emailBodyTemplate: '',
	clientType: null,
	billTemplateFileData: {
		fileId: '',
		fileHash: null,
		fileName: null,
	},
	billAdvertisingImageData: {
		fileId: '',
		fileHash: null,
		fileName: null,
	},
	billAdvertisingLink: null,
	priceType: null,
	tips0: null,
	tips1: null,
	tips2: null,
	tips3: null,
	defaultTips: null,
	ads: '',
	mixpanelToken: '',
	stopListSyncIntervalMinutes: 5,
	fetchedLanguages: [],
	IDSelectedCustomerDefaultLanguage: null,
	IDsSelectedCustomerLanguages: [],
	dataForSubmit: null,
	typeOfModalWindow: null,
};

export interface ICreateCustomerContext extends ICreateCustomerState {
	setFile(fileId: TFileId, sourceUrlLocally: TSourceUrlLocally): void;
	changeName(value: string): void;
	changeEmail(value: string): void;
	changeEmailBodyTemplate(value: string): void;
	setClientType(value: TCreateCustomerSelectedClientType): void;
	changeBillTemplateFileData(value: IFileData): void;
	changeBillAdvertisingImageData(value: IFileData): void;
	changeBillAdvertisingLink(value: string): void;
	changePriceType(value: string): void;
	setTips0(value: null | number): void;
	setTips1(value: null | number): void;
	setTips2(value: null | number): void;
	setTips3(value: null | number): void;
	setDefaultTips(value: TUseTipsByDefault): void;
	setAds(value: string): void;
	setMixpanelToken(value: string): void;
	setStopListSyncIntervalMinutes(value: Nullable<number>): void;
	setFetchedLanguages(value: IItemForGetShortInfo[]): void;
	setCustomerDefaultLanguage(value: TCreateCustomerSelectedItem): void;
	setCustomerLanguages(value: string[]): void;
	setDataForSubmit(data: ICreateCustomer | null): void;
	changeTypeOfModalWindow(value: TTypeOfCreateCustomerModalWindow): void;
	resetFields(): void;
}

export const CreateCustomerContext = createContext<ICreateCustomerContext>({
	setFile: () => undefined,
	changeName: () => undefined,
	changeEmail: () => undefined,
	changeEmailBodyTemplate: () => undefined,
	setClientType: () => undefined,
	changeBillTemplateFileData: () => undefined,
	changeBillAdvertisingImageData: () => undefined,
	changeBillAdvertisingLink: () => undefined,
	changePriceType: () => undefined,
	setTips0: () => undefined,
	setTips1: () => undefined,
	setTips2: () => undefined,
	setTips3: () => undefined,
	setDefaultTips: () => undefined,
	setAds: () => undefined,
	setMixpanelToken: () => undefined,
	setStopListSyncIntervalMinutes: () => undefined,
	setFetchedLanguages: () => undefined,
	setCustomerDefaultLanguage: () => undefined,
	setCustomerLanguages: () => undefined,
	setDataForSubmit: () => undefined,
	changeTypeOfModalWindow: () => undefined,
	resetFields: () => undefined,
	...createCustomerInitialState,
});
